import type { MessageKey } from './i18n/messages.ts';
import type { Tone } from './components/ui.tsx';

/**
 * Status vocabulary, in one place.
 *
 * The server's statuses are internal words — `partially_rolled_back` means
 * something precise to the engine and nothing to a customer. This is where
 * each one is given a sentence a person can read, and the single place a new
 * status has to be handled.
 */

export interface StatusPresentation {
  readonly label: MessageKey;
  readonly tone: Tone;
}

export const DEPLOYMENT_STATUS: Readonly<Record<string, StatusPresentation>> = {
  draft: { label: 'deployments.statusDraft', tone: 'neutral' },
  planning: { label: 'deployments.statusDraft', tone: 'neutral' },
  planned: { label: 'deployments.statusPlanned', tone: 'info' },
  awaiting_approval: { label: 'deployments.statusPlanned', tone: 'info' },
  queued: { label: 'deployments.statusQueued', tone: 'info' },
  running: { label: 'deployments.statusRunning', tone: 'accent' },
  verifying: { label: 'deployments.statusVerifying', tone: 'accent' },
  succeeded: { label: 'deployments.statusSucceeded', tone: 'success' },
  failed: { label: 'deployments.statusFailed', tone: 'danger' },
  cancelling: { label: 'deployments.statusCancelling', tone: 'warning' },
  cancelled: { label: 'deployments.statusCancelled', tone: 'neutral' },
  rolling_back: { label: 'deployments.statusRolledBack', tone: 'warning' },
  rolled_back: { label: 'deployments.statusRolledBack', tone: 'warning' },
  partially_rolled_back: { label: 'deployments.statusPartiallyRolledBack', tone: 'warning' },
};

/**
 * Statuses after which nothing more happens unless somebody asks.
 *
 * Broader than the engine's terminal set, and deliberately: a failed
 * deployment can still be retried, but a screen watching it should stop
 * waiting and show the customer what went wrong.
 */
export const TERMINAL_DEPLOYMENT_STATUSES: ReadonlySet<string> = new Set([
  'succeeded',
  'failed',
  'cancelled',
  'rolled_back',
  'partially_rolled_back',
]);

export const STEP_STATUS: Readonly<Record<string, StatusPresentation>> = {
  pending: { label: 'progress.stepPending', tone: 'neutral' },
  running: { label: 'progress.stepRunning', tone: 'info' },
  retrying: { label: 'progress.stepRunning', tone: 'warning' },
  success: { label: 'progress.stepSuccess', tone: 'success' },
  failed: { label: 'progress.stepFailed', tone: 'danger' },
  skipped: { label: 'progress.stepSkipped', tone: 'neutral' },
  rolling_back: { label: 'result.rolledBack', tone: 'warning' },
  rolled_back: { label: 'result.rolledBack', tone: 'warning' },
  rollback_failed: { label: 'progress.stepFailed', tone: 'danger' },
};

export const SERVER_STATUS: Readonly<Record<string, StatusPresentation>> = {
  draft: { label: 'servers.statusDraft', tone: 'neutral' },
  inspecting: { label: 'servers.statusInspecting', tone: 'info' },
  ready: { label: 'servers.statusReady', tone: 'success' },
  unreachable: { label: 'servers.statusUnreachable', tone: 'danger' },
  retired: { label: 'servers.statusRetired', tone: 'neutral' },
  in_use: { label: 'servers.statusInUse', tone: 'accent' },
};

/** Never leaves a customer looking at a raw internal word. */
export function presentation(
  table: Readonly<Record<string, StatusPresentation>>,
  status: string,
  fallback: StatusPresentation,
): StatusPresentation {
  return table[status] ?? fallback;
}
